import type { Condition } from './types.js'

export interface ConditionContext {
  /** Values of the current row or field group. */
  values: Record<string, unknown>
  /** Section-level control values, e.g. `percentages_undisclosed`. */
  controls?: Record<string, unknown>
  /** Values from the company profile tab, for cross-tab conditions such as bank metrics. */
  profile?: Record<string, unknown>
}

function isPresent(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (typeof v === 'string') return v.trim() !== ''
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'object' && 'amount' in (v as Record<string, unknown>)) {
    const amount = (v as { amount?: unknown }).amount
    return amount !== null && amount !== undefined && amount !== ''
  }
  return true
}

/** True for a money value that actually carries an amount (not blank, not undisclosed). */
function isMoneyWithAmount(v: unknown): boolean {
  if (typeof v !== 'object' || v === null) return false
  const money = v as { amount?: unknown; undisclosed?: unknown }
  if (!('amount' in money) && !('undisclosed' in money)) return false
  if (money.undisclosed === true) return false
  return money.amount !== null && money.amount !== undefined && money.amount !== ''
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : v === undefined || v === null ? [] : [v]
}

/** Evaluates a dictionary condition. Unknown shapes evaluate to false rather than throwing. */
export function evaluateCondition(cond: Condition | undefined, ctx: ConditionContext): boolean {
  if (!cond) return true
  if ('anyOf' in cond) return cond.anyOf.some((c) => evaluateCondition(c, ctx))
  if ('allOf' in cond) return cond.allOf.every((c) => evaluateCondition(c, ctx))
  if ('not' in cond) return !evaluateCondition(cond.not, ctx)
  if ('control' in cond) return Boolean(ctx.controls?.[cond.control])
  if ('profile' in cond) return ctx.profile?.[cond.profile] === cond.eq
  if ('anyAmountPresent' in cond) {
    // Only money-shaped values count: a date or a party list must not demand a currency.
    return Object.values(ctx.values).some((v) => isMoneyWithAmount(v))
  }
  if ('present' in cond) return isPresent(ctx.values[cond.field])
  if ('in' in cond) {
    const actual = asArray(ctx.values[cond.field])
    return actual.some((a) => cond.in.includes(String(a)))
  }
  if ('includesAny' in cond) {
    const actual = asArray(ctx.values[cond.field]).map(String)
    return cond.includesAny.some((c) => actual.includes(c))
  }
  if ('eq' in cond) {
    const actual = ctx.values[cond.field]
    if (Array.isArray(actual)) return actual.map(String).includes(String(cond.eq))
    return String(actual ?? '') === String(cond.eq)
  }
  return false
}

/** True when the field should be rendered and validated in this context. */
export function isFieldVisible(
  field: { visibleWhen?: Condition; hiddenWhen?: Condition },
  ctx: ConditionContext,
): boolean {
  if (field.hiddenWhen && evaluateCondition(field.hiddenWhen, ctx)) return false
  return evaluateCondition(field.visibleWhen, ctx)
}

/** True when the field must carry a value in this context. */
export function isFieldRequired(
  field: { required: string; requiredWhen?: Condition },
  ctx: ConditionContext,
): boolean {
  if (field.required === 'always') return true
  if (field.requiredWhen) return evaluateCondition(field.requiredWhen, ctx)
  return false
}

export { isPresent }
